import { useState } from "react";
import { IconFileText, IconLoader2 } from "@tabler/icons-react";
import { toast } from "sonner";
import { ArchivePayment, useInvoicesPdfMutation } from "../../api/queries";
import { getPaymentStatusBadge } from "../../utils/consts";
import { displayCurrencyAmount, displayISODate } from "../../utils/display";
import { base64ToPdfBlobUrl } from "../../utils/utilsFunctions/functions";

interface InvoiceItemProps {
  invoice: ArchivePayment;
}

export const InvoiceItem = ({ invoice }: InvoiceItemProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { mutateAsync: fetchPdf } = useInvoicesPdfMutation();
  const statusBadge = getPaymentStatusBadge(invoice.payment_status);

  const handleClick = async () => {
    setIsLoading(true);
    try {
      const response = await fetchPdf([invoice.external_id]);
      const result = response.results[0];

      if (result?.status === "success" && result.pdf_base64) {
        openPdfInNewTab(result.pdf_base64, invoice.external_id);
      } else {
        toast.error("Failed to load invoice PDF", {
          description: result?.error || "Unknown error occurred",
        });
      }
    } catch (error) {
      toast.error("Failed to load invoice PDF", {
        description: "Please try again later",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const openPdfInNewTab = (base64: string, externalId: string) => {
    const url = base64ToPdfBlobUrl(externalId, base64);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const formattedDate = displayISODate(
    invoice.date_created_in_accounting_software,
  );
  const formattedAmount = displayCurrencyAmount(
    invoice.amount,
    invoice.currency_type,
  );

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-blue-50 transition-colors text-left group disabled:opacity-70 disabled:cursor-wait"
    >
      <span className="text-emerald-500 group-hover:text-emerald-600">
        {isLoading ? (
          <IconLoader2 size={18} className="animate-spin" />
        ) : (
          <IconFileText size={18} />
        )}
      </span>
      <div className="flex flex-col flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-medium text-gray-700 truncate">
            Invoice #{invoice.external_id}
          </span>
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ring-inset shrink-0 ${statusBadge.className}`}
          >
            {statusBadge.label}
          </span>
        </div>
        <span className="text-xs text-gray-400">{formattedDate}</span>
      </div>
      <div className="flex flex-col items-end shrink-0">
        <span className="font-semibold text-gray-800">{formattedAmount}</span>
      </div>
    </button>
  );
};
